import React from 'react';
import { omit } from 'lodash';
import { useFormik } from 'formik';
import { navigate } from "gatsby";
import { ErrorMessage, Button, Label, Input, Checkbox, DateInput, Select } from '../../../../components/form';
import { PatientCareUserCreateRequest } from '../../../../api/patient-care/model/PatientCareUserCreateRequest';
import { UrlService } from '../../../../core/services/UrlService';
import { TrackingService } from '../../../../core/services/TrackingService';
import GoldenEyeCareLayout from '../components/layout/layout';
import SelectDate from '../../../../components/form/SelectDate';
import SelectYesNo from '../../../../components/form/SelectYesNo';
import SelectMonth from '../../../../components/form/SelectMonth';
import GoldenEyeCareSubsidyHero from '../components/layout/hero-subsidy';
import SelectOption from '../../../../components/form/SelectOption';
import Dropzone from '../../../../components/form/Dropzone';
import { PatientCareGoldenEyeCareService } from '../../../../api/patient-care/service/PatientCareGoldenEyeCareService';
import './index.scss';
import { useCmsCampaign } from '../../../../cms/hooks/useCmsCampaign';

type FormValues = PatientCareUserCreateRequest & {
    is_agree_terms_and_condition: boolean;
    is_agree_marketing: boolean;
    is_agree_finddoc_terms_and_condition: boolean;
    is_declared_infect: boolean;
    confirm_email: string;
    first_time_prove: string;
    first_time_date: string;
    med: string;
};

const GoldenEyeCareSponsorPage: React.FC = () => {
    const { dateRangeString } = useCmsCampaign('goldeneyecare');

    const [errorMsg, setErrorMsg] = React.useState<string>('');
    const { values, handleSubmit, handleChange, setFieldValue } = useFormik<FormValues>({
        initialValues: {
            name_en: '',
            name_zh: '',
            email: '',
            confirm_email: '',
            mobile: '',
            dialing_code: '+852',
            dob: '',
            first_time_prove: '',
            first_time_date: '',
            partial_hkid: '',
            phase: '1',
            med: 'unknown',
            is_agree_marketing: false,
            is_agree_terms_and_condition: false,
            is_agree_finddoc_terms_and_condition: false,
            is_declared_infect: false,
        },
        onSubmit: async (val: FormValues) => {
            try {
                const params = {
                    ...omit(val, ['is_agree_terms_and_condition', 'is_agree_finddoc_terms_and_condition', 'is_declared_infect', 'confirm_email']),
                    utm: TrackingService.getUTM(),
                }
                await PatientCareGoldenEyeCareService.create(params);
                TrackingService.trackCustomEvent({
                    category: 'campaigns',
                    action: `registration`,
                    label: 'goldeneyecare',
                    params: {
                        campaign_name: 'goldeneyecare',
                        step: 'registration',
                    }        
                });
                setErrorMsg('');
                navigate('/campaigns/goldeneyecare/success', { replace: true });
            } catch (err) {
                setErrorMsg(err.message);
            }
        },
    });

    const isAllFieldsFilled = React.useMemo(() => {
        return Object.keys(values).filter(x => x !== 'is_agree_marketing').map(key => values[key]).every((v) => !!v);
    }, [values]);

    return (
        <GoldenEyeCareLayout activePage="sponsor" skipEndDate={false}>
            <div className="subsidy-programme sponsor-page">
                <GoldenEyeCareSubsidyHero />
                <div className="divider">
                </div>
                <section className="content">
                    <div className='container'>
                        <div className="row intro">
                            <div className="col">
                                <h3>亮眼同行資助計劃</h3>
                                <p className="text-secondary-text">
                                    <p>
                                        由於濕性老年黃斑病變是長期慢性眼疾，黃斑水腫患者的血管有可能反覆增生而導致復發。 因此即使早前已完成初期治療後，視力有所改善，仍應積極治療以維持視力。有研究指出，中途停止打眼底針可能會令視力變差。
                                    </p>
                                    <p>
                                        亮眼同行計劃支援有需要而又符合申請資格的病人接受<strong style={{ fontSize: '1.2rem' }}>光學相干斷層掃瞄 (OCT)</strong>，作中長期的濕性老年黃斑病變檢查，及早發現血管增生情況，便能大大減低因黃斑水腫復發而視力變差的風險。
                                    </p>
                                    <p>
                                        申請只需簡單數個步驟，立即填寫資料，讓醫生為您進行檢查。
                                    </p>
                                </p>
                            </div>
                        </div>
                        <div className="row step-and-form">
                            <div className="col-lg-6">
                                <p className="important">
                                    計劃開始日期：{dateRangeString}
                                </p>
                                <p className="important">
                                    申請者條件：
                                </p>
                                <ol>
                                    <li>持有有效香港永久居民身份證；</li>
                                    <li>已年滿十八歲；</li>
                                    <li>持有有效轉數快(FPS)戶口；</li>
                                    <li>為私家眼科專科醫生之病人；</li>
                                    <li>被醫生確診為血管新生型（濕性）老年黃斑病變，並已在2022年一月或之後接受眼內注射藥物治療。</li>
                                </ol>
                                <p className="important">
                                    申請者限額：
                                </p>
                                <p>
                                    50位 （限額先到先得，請從速報名）
                                </p>
                                <p className="important">
                                    資助金額：
                                </p>
                                <p>
                                    每位成功申請者每次檢查可得到最高港幣$800資助額
                                </p>
                                <p>
                                    （三次檢查總共資助額為最高港幣$2,400）
                                </p>
                                <p className='mini' style={{ fontSize: '14px' }}>
                                    * 如該次檢查收費不少於港幣$800，申請者可得到港幣$800資助額<br/>
                                    * 如該次檢查收費少於港幣$800， 申請者則可得到等同實際收費金額的資助額
                                </p>
                                <p className="important">
                                    申請流程：
                                </p>
                                <ol>
                                    <li>填寫右方表格，並完成登記。</li>
                                    <li>成功登記後，確認電郵會在一個工作天內發送至您的郵箱。</li>
                                    <li>請細閱確認電郵內的資料，並自行於香港私家眼科醫生診所進行預約檢查。</li>
                                    <li>在預約時間到達診所後，醫生會為您進行檢查，您須在完成檢查後先進行付款。</li>
                                    <li>完成檢查後，您須按照確認電郵內網址的指示，上傳檢查收據的照片，以接收第二次檢查的確認電郵及換領券。您亦須在網址內提供您的收款資料，我們會在30天內將資助額存入您的戶口。</li>
                                    <li>同樣，您須自行於香港私家眼科醫生診所進行第二次的預約檢查，並在完成檢查後先進行付款。</li>
                                    <li>完成檢查後，請按照確認電郵內網址的指示，上傳檢查收據的照片，以接收第三次檢查的確認電郵及換領券。您亦須在網址內提供您的收款資料，我們會在30天內將資助額存入您的戶口。</li>
                                    <li>請自行於香港私家眼科醫生診所進行第三次的預約檢查，並在完成檢查後先進行付款。完成整個過程後，請按照確認電郵內網址的指示，上傳檢查收據的照片。您亦須在網址內提供您的收款資料，我們會在30天內將資助額存入您的戶口。</li>
                                </ol>
                                <p>
                                    <p className="important">
                                        申請者注意事項：
                                    </p>
                                </p>
                                <ol>
                                    <li>每人只可報名此計劃一次。</li>
                                    <li>此計劃只適用於確診為血管新生型（濕性）老年黃斑病變，並曾接受眼內注射藥物治療人士。</li>
                                    <li>申請者在換領券上指明的有效期內沒有上傳有效收據及填寫收款資料，有關資助會自動取消及不再補發。</li>
                                    <li>FindDoc 快德香港，黃金時代基金會及拜耳香港將保留任何爭議的最終決定權，包括隨時更改活動及其條款及細則，而毋須另行通知。</li>
                                </ol>
                                <p>
                                    如就此計劃有任何查詢，請致電+852 5579 5795或電郵至<a href="mailto:patientcare@finddoc.com">patientcare@finddoc.com</a>。
                                </p>
                            </div>
                            <div className="col-lg-6 application-form-col">
                                <form className="application-form" onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <Label
                                            htmlFor="name_en"
                                        >
                                            英文姓名（與身份證相同）
                                        </Label>
                                        <Input
                                            id="name_en"
                                            name="name_en"
                                            type="text"
                                            onChange={handleChange}
                                            value={values.name_en}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label htmlFor="name_zh">
                                            中文姓名（與身份證相同）
                                        </Label>
                                        <Input
                                            id="name_zh"
                                            name="name_zh"
                                            type="text"
                                            onChange={handleChange}
                                            value={values.name_zh}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label htmlFor="email">
                                            電郵地址
                                        </Label>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            onChange={handleChange}
                                            value={values.email}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label htmlFor="confirm_email">
                                            請再次確認電郵地址
                                        </Label>
                                        <Input
                                            id="confirm_email"
                                            name="confirm_email"
                                            type="confirm_email"
                                            onChange={handleChange}
                                            value={values.confirm_email}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label htmlFor="mobile">
                                            手機號碼
                                        </Label>
                                        <div className="input-group">
                                            <div className="input-group-prepend">
                                                <span className="input-group-text">{values.dialing_code}</span>
                                            </div>
                                            <Input
                                                id="mobile"
                                                name="mobile"
                                                type="text"
                                                onChange={handleChange}
                                                value={values.mobile}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <Label htmlFor="dob">
                                            出生日期 <span className="mini">(年-月-日)</span>
                                        </Label>
                                        <SelectDate
                                            id="dob"
                                            name="dob"
                                            value={values.dob}
                                            onChange={(e) => { setFieldValue('dob', e) }}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label htmlFor="partial_hkid">
                                            香港身份證號碼 (首 4 個號碼, 例: A123)
                                        </Label>
                                        <Input
                                            id="partial_hkid"
                                            name="partial_hkid"
                                            type="text"
                                            onChange={handleChange}
                                            value={values.partial_hkid}
                                            required
                                            maxLength={4}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label htmlFor="is_declared_infect">
                                            您是在私家醫生診所或醫院被確診為血管新生型（濕性）老年黃斑病變嗎？
                                        </Label>
                                        <SelectYesNo
                                            id="is_declared_infect"
                                            name="is_declared_infect"
                                            placeholder=""
                                            onChange={(e) => { setFieldValue('is_declared_infect', e) }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label htmlFor="first_time_date">
                                            您首次接受眼內注射藥物治療的日期：
                                            {/* applicants should not be accepted if the year/month is earlier than 2022/01 */}
                                        </Label>
                                        <SelectMonth
                                            id="first_time_date"
                                            name="first_time_date"
                                            value={values.first_time_date}
                                            onChange={(e) => { setFieldValue('first_time_date', e) }}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label htmlFor="first_time_prove">
                                            請上傳您第一次接受治療的醫生證明：(.png / .jpg) (檔案大小上限: 5MB)
                                        </Label>
                                        <Dropzone onChange={(e) => { setFieldValue('first_time_prove', e) }} />
                                    </div>
                                    {/* <div className="form-group">
                                        <Label htmlFor="med">
                                            你現在正接受那一個眼內注射藥物?
                                        </Label>
                                        <SelectOption
                                            id="med"
                                            name="med"
                                            placeholder=""
                                            value={values.med}
                                            options={[
                                                {
                                                    label: '雷珠單抗 (Ranibizumab)',
                                                    value: 'ranibizumab',
                                                },
                                                {
                                                    label: '阿柏西普 (Aflibercept)',
                                                    value: 'aflibercept',
                                                },
                                                {
                                                    label: '布羅魯珠單抗 (Brolucizumab)',
                                                    value: 'brolucizumab',
                                                },
                                                {
                                                    label: '其他',
                                                    value: 'other',
                                                },
                                                {
                                                    label: '我不清楚',
                                                    value: 'unknown',
                                                },
                                            ]}
                                            onChange={(e) => { setFieldValue('med', e) }}
                                        />
                                    </div> */}
                                    {/* <br /> */}
                                    <div className="form-group">
                                        <Checkbox
                                            onChange={handleChange}
                                            name="is_agree_marketing"
                                            id="is_agree_marketing"
                                            labelText={`我同意接收來自 FindDoc 的市場推廣電郵。`}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Checkbox
                                            onChange={handleChange}
                                            name="is_agree_finddoc_terms_and_condition"
                                            id="is_agree_finddoc_terms_and_condition"
                                            labelText={`我已閱讀及同意FindDoc的<a href="${UrlService.getBaseUrl('/terms')}">使用條款</a>和<a href="${UrlService.getBaseUrl('/privacy')}">私隱政策</a>。（*必選）`}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Checkbox
                                            onChange={handleChange}
                                            name="is_agree_terms_and_condition"
                                            id="is_agree_terms_and_condition"
                                            labelText={`我已細閱計劃申請流程及條款。（*必選）`}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            // data-ga-event-action="react-example-event-action"
                                            // data-ga-event-label="react-example-event-label"
                                            // data-ga-event-category="react-example-event-category"
                                            disabled={!isAllFieldsFilled}
                                        >
                                            提交
                                        </Button>
                                    </div>
                                    {errorMsg && <ErrorMessage message={errorMsg} shouldShowIcon={true} />}
                                </form>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </GoldenEyeCareLayout>
    );
};

export default GoldenEyeCareSponsorPage;
